import { formatDate, formatCurrency } from './formatters';
import type { 
  ClientReceivablesAging, 
  ClientBalanceSummary, 
  CommissionEntry, 
  CommissionSummary,
  ReconciliationAccount,
  ReconciliationReport 
} from '../types/Insurance';

/**
 * Export utilities for insurance reports
 * Provides CSV and Excel-compatible data export functionality
 */

export interface ExportOptions {
  filename?: string;
  includeHeaders?: boolean;
  dateFormat?: 'iso' | 'readable';
}

/**
 * Convert data to CSV format
 */
export const exportToCSV = (data: Record<string, unknown>[], columns: string[], options: ExportOptions = {}) => {
  const { filename = 'export', includeHeaders = true } = options;
  
  let csvContent = '';
  
  // Add headers
  if (includeHeaders) {
    csvContent += columns.join(',') + '\n';
  }
  
  // Add data rows
  data.forEach(row => {
    const values = columns.map(column => {
      const value = row[column];
      // Escape commas and quotes in CSV
      if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value || '';
    });
    csvContent += values.join(',') + '\n';
  });
  
  // Download the file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Export premium aging report to CSV
 */
export const exportPremiumAging = (data: ClientReceivablesAging[], asOfDate: Date) => {
  const exportData = data.map(item => ({
    'Client ID': item.clientId,
    'Cost Center ID': item.costCenterId || '',
    'Currency': item.currency,
    'Aging Bucket': item.agingBucket === 'current' ? 'Current' :
                   item.agingBucket === '30_days' ? '30 Days' :
                   item.agingBucket === '60_days' ? '60 Days' : '90+ Days',
    'Outstanding Amount': item.outstandingAmount,
    'As Of Date': formatDate(asOfDate.toISOString())
  }));

  exportToCSV(
    exportData, 
    ['Client ID', 'Cost Center ID', 'Currency', 'Aging Bucket', 'Outstanding Amount', 'As Of Date'],
    { filename: `premium-aging-${formatDate(asOfDate.toISOString()).replace(/\//g, '-')}` }
  );
};

/**
 * Export client balance summary to CSV
 */
export const exportClientBalances = (data: ClientBalanceSummary[], periodId?: string) => {
  const exportData = data.map(item => ({
    'Client Name': item.clientName,
    'Client Code': item.clientCode,
    'Premium Due': item.totalPremiumDue,
    'Payments Received': item.totalPaymentsReceived,
    'Outstanding Balance': item.outstandingBalance,
    'Currency': item.currency,
    'Last Payment Date': item.lastPaymentDate ? formatDate(item.lastPaymentDate) : '',
    'Period ID': periodId || 'Current'
  }));

  exportToCSV(
    exportData,
    ['Client Name', 'Client Code', 'Premium Due', 'Payments Received', 'Outstanding Balance', 'Currency', 'Last Payment Date', 'Period ID'],
    { filename: `client-balances-${periodId || 'current'}` }
  );
};

/**
 * Export commission summary to CSV
 */
export const exportCommissionSummary = (data: CommissionEntry[]) => {
  const exportData = data.map(item => ({
    'Agent Name': item.agentName,
    'Agent Code': item.agentCode,
    'Client Name': item.clientName,
    'Client Code': item.clientCode,
    'Policy Name': item.costCenterName,
    'Policy Code': item.costCenterCode,
    'Premium Amount': item.premiumAmount,
    'Commission Rate': `${(item.commissionRate * 100).toFixed(2)}%`,
    'Commission Due': item.commissionDue,
    'Commission Paid': item.commissionPaid,
    'Commission Outstanding': item.commissionOutstanding,
    'Status': item.status,
    'Due Date': formatDate(item.dueDate),
    'Paid Date': item.paidDate ? formatDate(item.paidDate) : '',
    'Currency': item.currency,
    'Policy Count': item.policyCount
  }));

  exportToCSV(
    exportData,
    ['Agent Name', 'Agent Code', 'Client Name', 'Client Code', 'Policy Name', 'Policy Code', 
     'Premium Amount', 'Commission Rate', 'Commission Due', 'Commission Paid', 'Commission Outstanding',
     'Status', 'Due Date', 'Paid Date', 'Currency', 'Policy Count'],
    { filename: `commission-summary-${new Date().toISOString().split('T')[0]}` }
  );
};

/**
 * Export reconciliation report to CSV
 */
export const exportReconciliationReport = (data: ReconciliationAccount[], periodId?: string) => {
  const exportData = data.map(item => ({
    'Account Code': item.accountCode,
    'Account Name': item.accountName,
    'GL Balance': item.glBalance,
    'Subledger Balance': item.subledgerBalance,
    'Variance': item.variance,
    'Currency': item.currency,
    'Last Reconciled': item.lastReconciled ? formatDate(item.lastReconciled) : '',
    'Status': parseFloat(item.variance) === 0 ? 'Reconciled' : 'Variance',
    'Period ID': periodId || 'Current'
  }));

  exportToCSV(
    exportData,
    ['Account Code', 'Account Name', 'GL Balance', 'Subledger Balance', 'Variance', 'Currency', 'Last Reconciled', 'Status', 'Period ID'],
    { filename: `reconciliation-report-${periodId || 'current'}` }
  );
};

/**
 * Export financial analytics summary
 */
export const exportFinancialAnalytics = (
  agingData: ClientReceivablesAging[],
  balanceData: ClientBalanceSummary[],
  commissionData: CommissionSummary | null,
  reconciliationData: ReconciliationReport | null,
  asOfDate: Date
) => {
  // Calculate summary metrics
  const totalOutstanding = agingData?.reduce((sum, item) => sum + parseFloat(item.outstandingAmount), 0) || 0;
  const currentReceivables = agingData?.filter(item => item.agingBucket === 'current')
    .reduce((sum, item) => sum + parseFloat(item.outstandingAmount), 0) || 0;
  const overdueReceivables = agingData?.filter(item => item.agingBucket !== 'current')
    .reduce((sum, item) => sum + parseFloat(item.outstandingAmount), 0) || 0;
  const varianceCount = reconciliationData?.accounts?.filter((acc) => parseFloat(acc.variance) !== 0).length || 0;

  const analyticsData = [
    {
      'Metric': 'Total Outstanding Receivables',
      'Value': formatCurrency(totalOutstanding.toString(), 'EUR'),
      'Category': 'Receivables'
    },
    {
      'Metric': 'Current Receivables',
      'Value': formatCurrency(currentReceivables.toString(), 'EUR'),
      'Category': 'Receivables'
    },
    {
      'Metric': 'Overdue Receivables',
      'Value': formatCurrency(overdueReceivables.toString(), 'EUR'),
      'Category': 'Receivables'
    },
    {
      'Metric': 'Active Clients',
      'Value': balanceData?.length?.toString() || '0',
      'Category': 'Portfolio'
    },
    {
      'Metric': 'Commission Outstanding',
      'Value': commissionData?.summary ? formatCurrency(commissionData.summary.totalOutstanding, 'EUR') : '0',
      'Category': 'Commissions'
    },
    {
      'Metric': 'Active Agents',
      'Value': commissionData?.summary?.agentCount?.toString() || '0',
      'Category': 'Commissions'
    },
    {
      'Metric': 'Reconciliation Variances',
      'Value': varianceCount.toString(),
      'Category': 'Reconciliation'
    },
    {
      'Metric': 'Report Date',
      'Value': formatDate(asOfDate.toISOString()),
      'Category': 'Metadata'
    }
  ];

  exportToCSV(
    analyticsData,
    ['Metric', 'Value', 'Category'],
    { filename: `financial-analytics-${formatDate(asOfDate.toISOString()).replace(/\//g, '-')}` }
  );
};
